import { useEffect, useState } from 'react'
import { Alert, Button, Flex, Tag, Typography, theme } from 'antd'
import { GiftOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { SubscriberStateTag } from '@/shared/ui/StatusTag'
import { MaskedPhone } from '@/shared/ui/MaskedPhone'
import { revealSubscriberContact } from '@/shared/api/contactReveal'
import { PERM } from '@/shared/auth/partnerPermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { FundPanel } from './FundPanel'
import { GrantPanel } from './GrantPanel'
import { takesCash, type DirectoryRow } from './cashdeskApi'
import type { CashPlan } from './cashdeskPlans'
import type { CashierState } from './useCashier'

export type Mode = 'menu' | 'fund' | 'grant'

/** Tarif narxining karrasi — tez tanlash tugmalari uchun */
const SUGGESTION_MULTIPLIERS = [1, 2, 3] as const

const ACTION_HEIGHT = 56

export interface SubscriberCardBodyProps {
  row: DirectoryRow
  cashier: CashierState
  plans: readonly CashPlan[]
  /** KANONIK paket nomi → narx; tez to'ldirish tugmalari shundan */
  priceByName: ReadonlyMap<string, Money>
  /**
   * «Abonentlar» ekranidan kelgan havola darhol ochadigan panel;
   * `null` — odatdagi menyu.
   *
   * ⚠️ Bu TAKLIF, buyruq emas: panel chizilmaydigan holatda (kassirning
   * tuguni yo'q, valyuta noma'lum) ekran MENYUGA qaytadi. Aks holda
   * qo'lda tahrirlangan URL bo'm-bo'sh kartochka berardi.
   */
  initialMode?: Mode | null
  onShowHistory?: (() => void) | null
}

/**
 * Abonent kartochkasining ICHI — kassir uchun QISQARTIRILGAN ko'rinish.
 *
 * <h2>⚠️ NEGA `Drawer` DAN AJRATILDI</h2>
 * Egasi talabi (2026-08-20): kompyuterda ish oqimi ekranning o'ng
 * chekkasidagi 460px lik chiziqda kechardi, qolgan ~1400px esa niqob
 * ostida qorayib turardi — qidiruv natijasi va kartochka BIR VAQTDA
 * ishlatib bo'lmasdi. `docs/11 §4.1` esa aynan ikki ustunni loyihalagan
 * edi. Endi bir xil ich IKKI qobiqda chiziladi: telefonda pastdan
 * chiqadigan `Drawer` (`SubscriberCardDrawer`), keng ekranda esa o'ng
 * ustundagi panel (`CashdeskPage`).
 *
 * <h2>⚠️ HOLAT SHU YERDA QOLADI</h2>
 * `mode` — kartochkaning ichki holati va u `row.id` o'zgarganda boshiga
 * qaytadi. Uni yuqoriga ko'tarish IKKI qobiqda ikki nusxa holat degani
 * bo'lardi.
 *
 * <h2>⚠️ PUL PANELLARINING UMRI O'ZGARMADI</h2>
 * `FundPanel` va `GrantPanel` avvalgidek shu komponent bilan birga
 * mount bo'ladi va shu bilan birga yo'qoladi, ya'ni idempotentlik
 * kalitining umri ham o'zgarmagan (`ConfirmAction` javadoc'i).
 */
export function SubscriberCardBody({
  row,
  cashier,
  plans,
  priceByName,
  initialMode = null,
  onShowHistory = null,
}: SubscriberCardBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()

  /**
   * Kassadagi ochish — AUDITLI, `contact.reveal` ostida.
   *
   * ⛔ `r.phone` — ABONENT yozuvining raqami, kontaktniki EMAS.
   * Kassa katalogidagi `row.phone` ham aynan shu maydondan keladi
   * (`SubscriberDirectoryView#phone`), ya'ni ochilgan qiymat ekranda
   * turgan niqobning O'ZINIKI.
   *
   * ⚠️ Huquqsiz kassirda `null` — `MaskedPhone` tugmani umuman
   * chizmaydi.
   */
  const revealPhone = can(PERM.contactReveal)
    ? (reason: string) => revealSubscriberContact(row.id, reason).then((r) => r.phone)
    : null
  const { token } = theme.useToken()
  const [mode, setMode] = useState<Mode>(initialMode ?? 'menu')

  // Boshqa abonent ochilganda rejim boshiga qaytadi — aks holda oldingi
  // abonent uchun ochilgan «Paket berish» yangi abonentga tegishli
  // bo'lib ko'rinardi.
  useEffect(() => {
    setMode(initialMode ?? 'menu')
  }, [row.id, initialMode])

  const planName = row.planName ?? null

  /*
    ⚠️ Paket NOMI bo'yicha topiladi, id bo'yicha emas.

    `SubscriberDirectoryView.Row` da `planId` ATAYLAB yo'q — u yopiq
    maydonlar ro'yxatiga kirmagan. Backend esa aynan KANONIK nomni
    beradi (`SubscriberServiceImpl.directory` → `planNamesById()`), va
    katalogdagi `name` ham kanonik. Ya'ni bu solishtiruv tarjimaga
    bog'liq emas: ekranda ko'rinadigan tarjima nom bilan solishtirish
    til almashtirilganda taklif tugmalarini jimgina yo'qotardi.

    Topilmasa — taklif ko'rsatilmaydi, xato ham chiqmaydi.
  */
  const planPrice = planName ? (priceByName.get(planName) ?? null) : null

  /** ⚠️ Narx noma'lum yoki nol bo'lsa taklif YO'Q — o'ylab topilgan son ko'rsatilmaydi */
  const suggestions: readonly Money[] =
    planPrice && planPrice.amount > 0
      ? SUGGESTION_MULTIPLIERS.map((k) => ({
          amount: planPrice.amount * k,
          currency: planPrice.currency,
        }))
      : []

  /**
   * ⚠️ Valyuta TAXMIN QILINMAYDI: u abonentning balansidan, kassirning
   * depozitidan yoki tarif narxidan olinadi — uchalasi ham serverdan
   * kelgan haqiqiy qiymat. Hech biri bo'lmasa summa maydoni umuman
   * chizilmaydi, chunki qaysi valyutada yozilayotgani noma'lum.
   */
  const currency =
    row.balance?.currency ?? cashier.balance?.balance.currency ?? planPrice?.currency ?? null

  /*
    ⚠️ AMALLAR HUQUQ KODI BILAN EMAS, SHERIK TURI BILAN filtrlanadi.

    ADR-029 §1 sherikning huquqlarini KODDA qotiradi — ular admin
    panelidagi kabi dinamik rol emas. Uchta yangi kod
    (`subscriber.directory`, `agent.fund`, `grant.issue`) backendda
    allaqachon bor, lekin kabinet karkasida huquq katalogi
    (`shared/auth/permissions`) hali yo'q — ya'ni bugun `can(...)` ni
    chaqiradigan joy mavjud emas.

    Bugun mavjud va MA'NOLI signal — sherik yozuvining turi
    (`GET /api/agents/roots` javobidagi `kind`, `takesCash`). U aynan
    shu savolga javob beradi: bu sherik naqd qabul qiladimi.

    ⚠️ Bu QULAYLIK, xavfsizlik EMAS: `agent.fund` ni server har so'rovda
    baribir tekshiradi va reseller yuborgan so'rov 403 bilan qaytadi.
    Katalog paydo bo'lgach shu bitta qator huquq tekshiruviga almashadi.
  */
  const canFund = takesCash(cashier.agent)

  const agentId = cashier.agent?.id ?? null

  /*
    ⚠️ MENYU — QOLDIQ HOLAT, «mode === 'menu'» EMAS.

    To'ldirish paneli faqat tugun va valyuta ma'lum bo'lganda chiziladi.
    Rejim tugmadan kelganda bu shart har doim bajarilgan (tugma o'sha
    paytda o'chiq turadi), lekin rejim endi URL'dan ham kelishi mumkin
    va o'shanda ikkala shart buzilishi mumkin. `mode === 'menu'` deb
    yozilsa ekranda HECH NARSA qolmasdi — na panel, na menyu.
  */
  const fundOpen = mode === 'fund' && agentId !== null && currency !== null
  const grantOpen = mode === 'grant'
  const menuOpen = !fundOpen && !grantOpen

  return (
    <Flex vertical gap={16}>
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {t('cashdesk.card.balance')}
        </Typography.Text>
        <Typography.Title
          level={1}
          style={{
            margin: 0,
            // Telefonda ham, kompyuterda ham ekranning eng katta soni
            fontSize: 'clamp(32px, 11vw, 44px)',
            lineHeight: 1.1,
            color: row.balance && row.balance.amount < 0 ? token.colorError : undefined,
          }}
        >
          {formatMoneyValue(row.balance)}
        </Typography.Title>
        {row.balance === null ? (
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            {t('cashdesk.card.balanceUnknown')}
          </Typography.Text>
        ) : null}
      </div>

      <Flex gap={6} wrap align="center">
        <SubscriberStateTag state={row.state} />
        {planName ? <Tag>{planName}</Tag> : null}
      </Flex>

      <Flex vertical gap={2}>
        {row.displayName ? <Typography.Text>{row.displayName}</Typography.Text> : null}
        {/* ⛔ RAQAM NIQOBLANGAN holda chiziladi (egasi qarori,
            2026-08-24). Server uni niqoblamaydi va bu ongli qaror
            (`SubscriberDirectoryView.java:50-54`: «ATAYLAB bor»),
            ya'ni niqob CHIZISH paytida qo'yiladi.

            ⚠️ Qo'ng'iroq qilish imkoni SAQLANADI — kassirning ishi
            aynan shu. «Ko'rsatish» bosilgandan keyin raqam `tel:`
            havolasi bo'lib qaytadi (`MaskedPhone`).

            ⛔ MAYDON `phone` — javobdagi ABONENT raqami, kontaktniki
            EMAS (`shared/api/contactReveal` dagi dalil). Kassada
            kontakt bloki umuman yo'q, ya'ni bu yerda faqat shu maydon
            ma'noli. */}
        {row.phone ? (
          <MaskedPhone value={row.phone} {...(revealPhone ? { onReveal: revealPhone } : {})} />
        ) : null}
      </Flex>

      {menuOpen ? (
        <Flex vertical gap={10}>
          {agentId === null ? (
            <Alert type="warning" showIcon message={t('cashdesk.noAgent')} />
          ) : null}

          {canFund ? (
            <Button
              type="primary"
              block
              size="large"
              icon={<PlusOutlined />}
              disabled={agentId === null || currency === null}
              style={{ height: ACTION_HEIGHT, fontWeight: 600 }}
              onClick={() => setMode('fund')}
            >
              {t('cashdesk.fund.action')}
            </Button>
          ) : null}

          {/* Paket berish IKKALA sherik turida ham bor: pul abonentning
              o'z balansidan yechiladi, sherikning depoziti tegilmaydi.

              ⚠️ Balansi YETMAGAN abonentda shu tugma BIRLASHGAN oqimni
              ochadi (§10-3): panel yetishmagan summani o'zi taklif
              qiladi va bitta tasdiq ostida avval to'ldiradi, keyin
              paket beradi. Shuning uchun uchinchi tugma qo'shilmadi. */}
          <Button
            block
            size="large"
            icon={<GiftOutlined />}
            style={{ height: ACTION_HEIGHT }}
            onClick={() => setMode('grant')}
          >
            {t('cashdesk.grant.action')}
          </Button>

          {onShowHistory ? (
            <Button block size="large" icon={<HistoryOutlined />} onClick={onShowHistory}>
              {t('history.open')}
            </Button>
          ) : null}

          {!canFund && cashier.agent !== null ? (
            <Alert type="info" showIcon message={t('cashdesk.resellerNoFund')} />
          ) : null}
        </Flex>
      ) : null}

      {fundOpen && agentId !== null && currency !== null ? (
        <FundPanel
          subscriber={row}
          agentId={agentId}
          available={cashier.balance?.available ?? null}
          perTxnLimit={cashier.perTxnLimit}
          suggestions={suggestions}
          currency={currency}
          onBack={() => setMode('menu')}
        />
      ) : null}

      {grantOpen ? (
        <GrantPanel
          subscriber={row}
          plans={plans}
          agentId={agentId}
          available={cashier.balance?.available ?? null}
          perTxnLimit={cashier.perTxnLimit}
          canFund={canFund}
          onBack={() => setMode('menu')}
        />
      ) : null}
    </Flex>
  )
}
